/**
 * The stack table: **one row per LEVEL, with the LAYERS drawn between the
 * rows as the gaps they are.**
 *
 * N levels give N−1 layers. That arithmetic is the thing people get wrong on
 * paper (five materials need six levels), so this component never asks for a
 * layer count and never states one as a fact to be believed — it draws the
 * gaps. Add a sixth level and a fifth layer appears between the rows, with its
 * own thickness cell.
 *
 * Values in, patches out: every control is bound through the caller's form, so
 * the dialog owns validation and this file owns only the shape of the table.
 */
import { Plus, Trash2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import type { UseFormReturn } from 'react-hook-form';

import type { LayersQuery } from '@/graphql/graphql';
import type { FeatureResult } from '@/hooks/useFeature';
import {
  MAX_LEVELS,
  type LayerStackDerived,
  type LayerStackWarning,
  type LayerStackWarningKey,
} from '@/lib/layerStack/dialogModel';

import { Button } from '@/components/ui/button';
import { FormControl, FormDescription, FormField, FormItem, FormLabel } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { NumericField } from '@/components/ui/numeric-field';
import { SurfacePicker, type SurfaceSide } from '@/components/SurfacePicker';
import { UpgradeBadge } from '@/components/subscription/FeatureGate';

type LayerData = LayersQuery['layers'][number];

/** Literal keys, not a `layerStack.warnings.${…}` template: i18next's
 *  generated key union only accepts keys it can see statically (same reason
 *  `ExpectationFields`' `MODE_LABEL_KEYS` exists). */
const WARNING_LABEL_KEYS: Record<
  LayerStackWarningKey,
  'layerStack.warnings.toleranceTooFine' | 'layerStack.warnings.layersWithoutDesign'
> = {
  toleranceTooFine: 'layerStack.warnings.toleranceTooFine',
  layersWithoutDesign: 'layerStack.warnings.layersWithoutDesign',
};

interface Props {
  form: UseFormReturn<any>;
  layers: LayerData[];
  /** Live level values (watched by the dialog), used for the derived read-only
   *  cells. VALUES only — identity comes from `fields`. */
  levels: { name: string; designLayerId: string; asBuiltLayerId: string }[];
  /**
   * `useFieldArray`'s own rows, for their `id` and nothing else. React's
   * documented react-hook-form pattern: iterate for IDENTITY here, read
   * VALUES from `levels`. A level's position is not its identity — remove a
   * middle level and every row below it shifts up one index, so an
   * index-keyed row makes React reuse the component that was already there,
   * complete with the removed level's text still sitting in its inputs.
   */
  fields: { id: string }[];
  derived: LayerStackDerived;
  warnings: LayerStackWarning[];
  gate: Pick<FeatureResult, 'allowed' | 'gate' | 'promptUpgrade'>;
  onAddLevel: () => void;
  onRemoveLevel: (index: number) => void;
  /** Per-level inline error copy, already translated by the dialog. */
  levelErrors: (index: number) => string[];
  /** Stack-wide inline error copy ("at least two levels", …). */
  stackErrors: string[];
}

/** A level's display name, falling back to its position while the box is empty. */
const displayName = (name: string, index: number, fallback: (n: number) => string) =>
  name.trim() || fallback(index + 1);

export function LayerStackFields({
  form,
  layers,
  levels,
  fields,
  derived,
  warnings,
  gate,
  onAddLevel,
  onRemoveLevel,
  levelErrors,
  stackErrors,
}: Props) {
  const { t } = useTranslation('project');
  const fallbackName = (n: number) => t('layerStack.dialog.levelFallbackName', { n });
  const atMax = derived.levelCount >= MAX_LEVELS;

  return (
    <div data-testid='stack-fields' className='flex min-w-0 flex-col gap-3'>
      <div className='flex items-center gap-2'>
        <FormLabel className={gate.allowed ? '' : 'opacity-60'}>{t('layerStack.dialog.levels')}</FormLabel>
        {!gate.allowed && <UpgradeBadge gate={gate.gate} />}
        {/* The arithmetic, stated as a running total of what is on screen —
            never as a question. */}
        <span data-testid='stack-counts' className='ml-auto text-xs text-slate-600 dark:text-stone-400'>
          {t('layerStack.dialog.levelsToLayers', { levels: derived.levelCount, layers: derived.layerCount })}
        </span>
      </div>

      {stackErrors.map((msg) => (
        <p key={msg} data-testid='stack-error' className='text-sm text-destructive'>
          {msg}
        </p>
      ))}

      {levels.map((lvl, i) => {
        const errors = levelErrors(i);
        // From the model, not re-derived here: `deriveLayerStack` already
        // decides what "pending" means (`isLevelPending`), and a second copy
        // of that rule is a second thing to get wrong.
        const pending = derived.pendingIndices.includes(i);
        const pair = i < levels.length - 1 ? levels[i + 1] : undefined;
        const bothDesigned = !!lvl.designLayerId && !!pair?.designLayerId;
        return (
          // Keyed by the field array's stable id, NEVER by `i`: the field
          // paths below are index-based, but the ROW is not — see the `fields`
          // prop.
          <div key={fields[i]?.id ?? `level-${i}`} className='contents'>
            <div
              data-testid={`stack-level-${i}`}
              // Counted by this attribute rather than by a `data-testid^=`
              // prefix: the row's own children (`stack-level-0-under-tol`, …)
              // share that prefix, so a prefix count would silently include
              // them and stop measuring the number of ROWS at all.
              data-stack-row='level'
              className='min-w-0 rounded-md border border-slate-200 p-3 dark:border-stone-700'>
              <div className='flex min-w-0 items-end gap-2'>
                <FormField
                  control={form.control}
                  name={`levels.${i}.name`}
                  render={({ field }) => (
                    <FormItem className='min-w-0 flex-1'>
                      <FormLabel>{t('layerStack.dialog.levelName')}</FormLabel>
                      <FormControl>
                        <Input
                          {...field}
                          data-testid={`stack-level-${i}-name`}
                          placeholder={t('layerStack.dialog.levelNamePlaceholder')}
                        />
                      </FormControl>
                    </FormItem>
                  )}
                />
                {pending && (
                  // Absence is a STATE, not a failure: a level nobody has
                  // surveyed yet is the normal condition of a part-built
                  // pavement. Never styled as an error, never blocking.
                  <span
                    data-testid={`stack-level-${i}-pending`}
                    title={t('layerStack.dialog.pendingHelp')}
                    className='mb-2 shrink-0 rounded border border-slate-300 px-2 py-0.5 text-xs text-slate-600 dark:border-stone-600 dark:text-stone-300'>
                    ○ {t('layerStack.dialog.pending')}
                  </span>
                )}
                <Button
                  type='button'
                  variant='ghost'
                  size='sm'
                  className='mb-1 shrink-0'
                  data-testid={`stack-level-${i}-remove`}
                  aria-label={t('layerStack.dialog.removeLevel')}
                  onClick={() => onRemoveLevel(i)}>
                  <Trash2 className='size-4' />
                </Button>
              </div>

              <div className='mt-3 grid gap-3 sm:grid-cols-2'>
                {/* `SurfacePicker`, not a bare `<Select>`: it supplies
                    `pcMethod` AND `surfaceIds`, both of which a plain select
                    drops silently. */}
                <SurfacePicker
                  form={form}
                  prefix={`levels.${i}.design` as SurfaceSide}
                  layers={layers}
                  label={t('layerStack.dialog.design')}
                />
                <SurfacePicker
                  form={form}
                  prefix={`levels.${i}.asBuilt` as SurfaceSide}
                  layers={layers}
                  label={t('layerStack.dialog.asBuilt')}
                />
              </div>

              <div className='mt-3 grid gap-3 sm:grid-cols-2'>
                <FormField
                  control={form.control}
                  name={`levels.${i}.underTolMm`}
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t('layerStack.dialog.underTol')}</FormLabel>
                      <FormControl>
                        <NumericField
                          value={field.value ?? null}
                          onChange={field.onChange}
                          onClear={() => field.onChange(null)}
                          step='1'
                          min='0'
                          data-testid={`stack-level-${i}-under-tol`}
                        />
                      </FormControl>
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name={`levels.${i}.overTolMm`}
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t('layerStack.dialog.overTol')}</FormLabel>
                      <FormControl>
                        <NumericField
                          value={field.value ?? null}
                          onChange={field.onChange}
                          onClear={() => field.onChange(null)}
                          step='1'
                          min='0'
                          data-testid={`stack-level-${i}-over-tol`}
                        />
                      </FormControl>
                    </FormItem>
                  )}
                />
              </div>
              <FormDescription className='mt-1'>{t('layerStack.dialog.toleranceHelp')}</FormDescription>
              {errors.map((msg) => (
                <p key={msg} data-testid={`stack-level-${i}-error`} className='mt-1 text-sm text-destructive'>
                  {msg}
                </p>
              ))}
            </div>

            {/* THE LAYER. Drawn in the gap between two level rows, because
                that is exactly what it is: the material between them. There
                are always one fewer of these than there are rows above and
                below — which is the point. */}
            {pair &&
              (() => {
                // Computed once so the truncated span and its `title` (the
                // hover fallback for the text this same truncation hides)
                // never drift from one another.
                const layerLabel = t('layerStack.dialog.layerLabel', {
                  n: i + 1,
                  upper: displayName(lvl.name, i, fallbackName),
                  lower: displayName(pair.name, i + 1, fallbackName),
                });
                return (
                  <div
                    data-testid={`stack-layer-${i}`}
                    data-stack-row='layer'
                    className='mx-6 flex min-w-0 items-center gap-2 border-l-2 border-dashed border-slate-300 py-1 pl-3 text-xs dark:border-stone-600'>
                    {/* `min-w-0` + `truncate` on the span itself, not just the
                        row: a flex child's default `min-width: auto` lets its
                        text refuse to shrink, which pushes the row (and the
                        dialog) wide even though the row already has
                        `min-w-0`. The composed two-name label is the thing
                        most likely to be long, so it gets the `title`
                        fallback. */}
                    <span className='min-w-0 truncate font-medium' title={layerLabel}>
                      {layerLabel}
                    </span>
                    <span
                      data-testid={`stack-layer-${i}-thickness`}
                      className='ml-auto shrink-0 text-slate-600 dark:text-stone-400'
                      title={
                        bothDesigned
                          ? t('layerStack.dialog.layerThicknessFromDesign')
                          : t('layerStack.dialog.layerThicknessNoDesign')
                      }>
                      {/* An unknown thickness is an em dash — never `0`, never
                          `NaN` (global constraints). The design thickness is the
                          gap between the two DESIGN surfaces, which only the run
                          itself can measure. */}
                      {t('layerStack.dialog.layerThickness')}: —
                    </span>
                  </div>
                );
              })()}
          </div>
        );
      })}

      <div className='flex items-center gap-2'>
        <Button
          type='button'
          variant='outline'
          size='sm'
          data-testid='stack-add-level'
          // Never the HTML `disabled` attribute for the GATE (CLAUDE.md): it
          // swallows the click, and the click is what opens the upgrade
          // dialog. `disabled` is used only for the data condition (the hard
          // level ceiling), which no upgrade can lift.
          disabled={gate.allowed && atMax}
          aria-disabled={gate.allowed ? undefined : true}
          data-locked={gate.allowed ? undefined : 'true'}
          onClick={() => {
            if (!gate.allowed) return gate.promptUpgrade();
            onAddLevel();
          }}>
          <Plus className='mr-1 size-4' /> {t('layerStack.dialog.addLevel')}
        </Button>
        <span className='text-xs text-slate-600 dark:text-stone-400'>
          {atMax ? t('layerStack.dialog.maxLevels', { max: MAX_LEVELS }) : t('layerStack.dialog.addLevelHelp')}
        </span>
      </div>

      {warnings.length > 0 && (
        <div data-testid='stack-warnings' className='flex flex-col gap-1'>
          {warnings.map((w) => (
            <p key={w.key} className='text-xs text-amber-700 dark:text-amber-400'>
              {/* Advisory only — these never block a run. */}
              {t(WARNING_LABEL_KEYS[w.key], w.params)}
            </p>
          ))}
        </div>
      )}
    </div>
  );
}
